import { useState, useEffect } from 'react';
import { Plus, FileText, Trash2, Edit2, Globe, Lock, ExternalLink } from 'lucide-react';
import { adminAxios } from './AdminLayout';
import { uploadFile as uploadFileFn } from '../../utils/fileUpload';

var BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDocuments() {
  var [docs, setDocs] = useState([]);
  var [loading, setLoading] = useState(true);
  var [showModal, setShowModal] = useState(false);
  var [editDoc, setEditDoc] = useState(null);

  function fetchDocs() {
    adminAxios().get('/api/admin/documents').then(function(r) { setDocs(r.data); setLoading(false); });
  }
  useEffect(function() { fetchDocs(); }, []);

  async function handleDelete(id) {
    if (!window.confirm('Delete this document?')) return;
    await adminAxios().delete('/api/admin/documents/' + id);
    fetchDocs();
  }

  var typeLabels = { terms: 'Terms & Conditions', compliance: 'Compliance', announcement: 'Announcement', corporate: 'Corporate', general: 'General' };

  return (
    <div data-testid="admin-documents-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-outfit text-2xl font-bold text-slate-900">Documents</h2>
        <button onClick={function(){setEditDoc(null);setShowModal(true);}} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0096D6] text-white font-manrope text-sm font-semibold">
          <Plus className="w-4 h-4" /> Add Document
        </button>
      </div>

      {loading ? <div className="font-manrope text-slate-500">Loading...</div> :
       docs.length === 0 ? <div className="text-center py-12 bg-white rounded-2xl border border-slate-100"><FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="font-manrope text-slate-500">No documents yet</p></div> :
       <div className="space-y-3">
        {docs.map(function(doc) {
          return (
            <div key={doc.id} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-[#0096D6]" />
                <div>
                  <h4 className="font-manrope font-semibold text-slate-900">{doc.title}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs font-manrope px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{typeLabels[doc.doc_type] || doc.doc_type}</span>
                    {doc.public ? <span className="text-xs font-manrope text-green-600 flex items-center gap-1"><Globe className="w-3 h-3" /> Public</span> : <span className="text-xs font-manrope text-slate-400 flex items-center gap-1"><Lock className="w-3 h-3" /> Private</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {doc.public_url && <a href={doc.public_url} target="_blank" rel="noreferrer" className="p-2 hover:bg-slate-100 rounded-lg" title="View public link"><ExternalLink className="w-4 h-4 text-slate-500" /></a>}
                {doc.download_url && <a href={doc.download_url} target="_blank" rel="noreferrer" className="p-2 hover:bg-slate-100 rounded-lg" title="Download"><FileText className="w-4 h-4 text-slate-500" /></a>}
                <button onClick={function(){setEditDoc(doc);setShowModal(true);}} className="p-2 hover:bg-slate-100 rounded-lg"><Edit2 className="w-4 h-4 text-slate-500" /></button>
                <button onClick={function(){handleDelete(doc.id);}} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
              </div>
            </div>
          );
        })}
       </div>
      }

      {showModal && <DocModal doc={editDoc} onClose={function(){setShowModal(false);setEditDoc(null);}} onSaved={function(){setShowModal(false);setEditDoc(null);fetchDocs();}} />}
    </div>
  );
}

function DocModal({ doc, onClose, onSaved }) {
  var isEdit = !!doc?.id;
  var [form, setForm] = useState({
    title: '', doc_type: 'general', public: false, content_html: '', file_id: '',
    ...(doc || {}),
  });
  var [saving, setSaving] = useState(false);
  var [uploading, setUploading] = useState(false);

  async function handleFileUpload(e) {
    var file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      var uploaded = await uploadFileFn(file, 'document');
      setForm(Object.assign({}, form, { file_id: uploaded.id }));
    } catch (err) { alert(typeof err.message === 'string' ? err.message : 'Upload failed'); }
    finally { setUploading(false); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) await adminAxios().put('/api/admin/documents/' + doc.id, form);
      else await adminAxios().post('/api/admin/documents', form);
      onSaved();
    } catch (err) { alert(typeof (err.response&&err.response.data&&err.response.data.detail)==='string'?err.response.data.detail:'Failed'); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 m-4 shadow-2xl" onClick={function(e){e.stopPropagation();}}>
        <h3 className="font-outfit text-xl font-bold text-slate-900 mb-4">{isEdit ? 'Edit Document' : 'Add Document'}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="font-manrope text-xs font-medium text-slate-600 block mb-1">Title *</label>
            <input value={form.title} onChange={function(e){setForm(Object.assign({},form,{title:e.target.value}));}} className="w-full px-3 py-2 rounded-lg border border-slate-200 font-manrope text-sm" required />
          </div>
          <div>
            <label className="font-manrope text-xs font-medium text-slate-600 block mb-1">Type</label>
            <select value={form.doc_type} onChange={function(e){setForm(Object.assign({},form,{doc_type:e.target.value}));}} className="w-full px-3 py-2 rounded-lg border border-slate-200 font-manrope text-sm">
              <option value="terms">Terms & Conditions</option>
              <option value="compliance">Compliance</option>
              <option value="announcement">Announcement</option>
              <option value="corporate">Corporate</option>
              <option value="general">General</option>
            </select>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={form.public} onChange={function(e){setForm(Object.assign({},form,{public:e.target.checked}));}} className="w-4 h-4 rounded" id="doc-public" />
            <label htmlFor="doc-public" className="font-manrope text-sm text-slate-700">Make publicly accessible</label>
          </div>
          <div>
            <label className="font-manrope text-xs font-medium text-slate-600 block mb-1">Upload File (PDF, Word, etc.)</label>
            <input type="file" onChange={handleFileUpload} className="font-manrope text-sm" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.jpg,.png" />
            {uploading && <p className="text-xs text-slate-500 mt-1">Uploading...</p>}
            {form.file_id && <p className="text-xs text-green-600 mt-1">File attached</p>}
          </div>
          <div>
            <label className="font-manrope text-xs font-medium text-slate-600 block mb-1">Or paste HTML content</label>
            <textarea value={form.content_html} onChange={function(e){setForm(Object.assign({},form,{content_html:e.target.value}));}} className="w-full px-3 py-2 rounded-lg border border-slate-200 font-manrope text-sm h-24 resize-none" placeholder="<p>Your document content...</p>" />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-slate-200 font-manrope text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 rounded-xl bg-[#0096D6] text-white font-manrope text-sm font-semibold disabled:opacity-50">{saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
